import React, { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { generateEmailTemplate, getEmailCampaignContacts, getQuestionAnswer, saveEmailCampaignContact, SendCampaignMarketing, updateEmailCampaign, updateStatusEmailCampaign } from 'services/campaign.service';
import DOMPurify from 'dompurify';
import { useDispatch } from 'react-redux';
import { getContact } from 'slices/contact/thunk';
import { toast } from 'react-toastify';
import { getCampaigns } from 'slices/campaign/thunk';
import DeleteModal from 'Common/DeleteModal';
import {
  CampaignSetupStep,
  ReviewStep,
  ContactStep,
  ConfirmationStep,
  SendingStep,
  ProgressIndicator,
  StepNavigation,
  ChannelType,
  CampaignType,
  MetaData,
  CampaignQuestionType,
  CampaignTypeText
} from './components';
import './EditorConvertToHTML.scss';
import { getSaleRepOptions, searchByKeyword } from 'services/contact.service';
import { ArrowLeft } from 'lucide-react';
import { simulateApiDelay } from 'pages/Knowledge/utils';
import { getGeneralSettingsByKey } from 'services/settings.service';
import { use } from 'i18next';

interface ProgressCampaignProps {
  isInProgress: boolean;
  isEditProgress: boolean;
  isViewProgress: boolean;
  eventData: any;
  cancelProgress: () => void;
}

const ProgressCampaign: React.FC<ProgressCampaignProps> = ({ isInProgress, isEditProgress, isViewProgress, eventData, cancelProgress }) => {
  const dispatch = useDispatch<any>();

  const [currentStep, setCurrentStep] = useState(1);
  const [buttonName, setButtonName] = useState('Next');
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [contactsSelected, setContactsSelected] = useState<any[]>([]);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [emailCampaignId, setEmailCampaignId] = useState<string>();
  const [questionAnswer, setQuestionAnswer] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [sanitizedTemplate, setSanitizedTemplate] = useState('');
  const [isModalDeleteVisible, setIsModalDeleteVisible] = useState(false);
  const [showErrorForm, setShowErrorForm] = useState(false);
  const [showErrorSelectContact, setShowErrorSelectContact] = useState(false);
  const [currentEmailCampaignData, setCurrentEmailCampaignData] = useState<any>(null);
  const [waitingReGenerateTemplate, setWaitingReGenerateTemplate] = useState<boolean>(false);
  const [contactList, setContactList] = useState<any[]>([]);
  const [contactsTemp, setContactsTemp] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubjectChanged, setIsSubjectChanged] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [textSearchContact, setTextSearchContact] = useState('');
  const [zipCodeFilter, setZipCodeFilter] = useState('');
  const [rangeFilter, setRangeFilter] = useState('');
  const [statusName, setStatusName] = useState<any>();
  const [skipCount, setSkipCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [count, setCount] = useState(0);
  const [channelType, setChannel] = useState('');
  const [saleRepOptions, setSaleRepOptions] = useState<any>(null)
  const [saleRepName, setSaleRepName] = useState<any>([]);
  const [dataTemp, setDataTemp] = useState<any>(null);
  const [contentChanged, setContentChanged] = useState<boolean>(false);
  const [reviewLink, setReviewLink] = useState<string>('');
  const [showErrorReviewLink, setShowErrorReviewLink] = useState(false);


  const limit = 20;

  useEffect(() => {
    if (currentStep < 1) {
      setButtonName('Next')
      cancelProgress();
      setCurrentStep(1);
      setEmailTemplate('');
      setContactsTemp([]);
      setIsEditing(false);
      setChannel('')
      setEmailCampaignId('');
      formik.resetForm();
    }
    if (currentStep === 1) {
      if (isViewProgress) {
        setButtonName('Next')
      } else {
        setButtonName('Generate Template')
      }
    }
    if (currentStep === 2) {
      setButtonName('Next');
    }
    if (currentStep === 3) {
      loadContacts(0, true, textSearchContact, zipCodeFilter, rangeFilter, statusName, saleRepName, channelType);

      // if (!!isEditProgress && eventData?.id) {
      //   getEmailCampaignContacts(eventData?.id)
      //     .then((res: any) => {
      //       if (res.every((contact: any) => contactsTemp.some((selected: any) => selected.email === contact.email))) {
      //         return;
      //       }
      //       setContactsTemp(res);
      //     })
      //     .catch((error: any) => {
      //       console.error('Error fetching email campaign contacts:', error);
      //     });
      // }

      return;
    }
    if (currentStep === 4) {
      setButtonName('Send')
    }
  }, [currentStep, cancelProgress, eventData, isEditProgress])

  useEffect(() => {
    setSanitizedTemplate(DOMPurify.sanitize(emailTemplate));
  }, [emailTemplate])

  useEffect(() => {
    const fetchReviewLink = async () => {
      dispatch(getContact({}))
      const reviewLink = await getGeneralSettingsByKey('GoogleReviewLink')
      if (reviewLink) {
        setReviewLink(reviewLink?.value);
      } else {
        setReviewLink('');
      }
    };
    fetchReviewLink();
  }, [dispatch]);

  useEffect(() => {
    formik.resetForm();
    getQuestionAnswer()
      .then((res: any) => {
        if (res) {
          setQuestionAnswer(res);
        }
      })
      .catch((err: any) => {
        console.error('Error fetching question answers:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isEditProgress && eventData) {
      setCurrentStep(1);
      setEmailTemplate(eventData?.emailTemplate || '');
      setEmailCampaignId(eventData?.id || '');
      formik.setFieldValue('name', eventData.name);
      formik.setFieldValue('answers', Array.isArray(questionAnswer) ? questionAnswer?.map((question: any) => ({
        questionText: question.questionText,
        answerText: isEditProgress
          ? eventData?.selectedAnswers?.find((answer: any) => answer.questionText === question.questionText)?.answerText || ''
          : '',
      })) : []);
      const matchedChannel = questionAnswer?.find((answer: any) =>
        (answer.answerText === ChannelType.Email) ||
        (answer.answerText === ChannelType.SMS) ||
        (answer.answerText === ChannelType.EmailAndSMS)
      ) || eventData?.channel;

      getEmailCampaignContacts(eventData?.id)
        .then((res: any) => {
          if (res.every((contact: any) => contactsTemp.some((selected: any) => selected.email === contact.email))) {
            return;
          }
          setContactsTemp(res);
        })
        .catch((error: any) => {
          console.error('Error fetching email campaign contacts:', error);
        });
      setChannel(matchedChannel ? matchedChannel : null);
      getSaleRepOptions().then((res: any) => {
        setSaleRepOptions(res);
      })
    } else {
      console.log(channelType);
      setCurrentStep(1);
      setEmailTemplate('');
      setContactsSelected([]);
      setSelectAll(false);
      setCurrentSubject(null);
      setChannel('');
      setEmailCampaignId('');
      setContactsTemp([]);
    }
  }, [eventData, isEditProgress]);

  useEffect(() => {
    // if (!!contactList && contactList.length > 0) {
    //   contactList.forEach(contact => {
    //     if (Array.isArray(contactsTemp) && contactsTemp.some(existingContact => existingContact.email === contact.email)) {
    //       setContactsSelected([...contactsSelected, contact])
    //     }
    //   });
    // }

    const areArraysEqual =
      contactList.length > 0
        ? contactsSelected.length > 0 ? contactList.every((selected: any) => contactsSelected.some((contact: any) => contact.email === selected.email)) : false : false

    if (!areArraysEqual) {
      setSelectAll(false);
    } else {
      setSelectAll(true);
    }
  }, [contactList])

  useEffect(() => {
    getSaleRepOptions().then((res: any) => {
      setSaleRepOptions(res);
    })
  }, [])

  useEffect(() => {
    if (currentStep === 3 && contactList.length === 0) {
      setShowErrorSelectContact(true);
    }
  }, [contactsSelected, currentStep])

  // Handle "Select all" checkbox toggle
  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      // Check if contactsSelected has items
      if (contactsSelected.length > 0) {
        // Check each item in contactList if it doesn't exist in contactsSelected
        const newContacts = contactList.filter(contact =>
          !contactsSelected.some(selected => selected.id === contact.id)
        );
        // Add only the new contacts to contactsSelected
        setContactsSelected([...contactsSelected, ...newContacts]);
      } else {
        // If no contacts selected, add all contactList
        setContactsSelected([...contactList]);
      }
      setShowErrorSelectContact(false)
    } else {
      setContactsSelected([]);
    }
  };

  // Handle individual contact checkbox toggle
  const handleContactSelect = (item: any) => {
    let updatedSelection;
    if (contactsSelected.some((contact) => contact.id === item.id)) {
      updatedSelection = contactsSelected.filter((contact) => contact.id !== item.id);
      setContactsSelected(updatedSelection);
    } else {
      updatedSelection = [...contactsSelected, item];
      setContactsSelected(updatedSelection);
    }

    // Update "Select all" state based on whether all contacts are selected
    setSelectAll(updatedSelection.length === contactList.length);
  };

  const handleSaveEmailCampaignContact = async () => {
    let payload;
    if (channelType === ChannelType.Email) {
      payload = contactsSelected.map((contact) => ({
        name: contact.name,
        email: contact.email,
        phoneNumber: '',
        contactId: contact.id,
      }));
    } else {
      payload = contactsSelected.map((contact) => ({
        name: contact.name,
        email: '',
        phoneNumber: contact.phoneNumber,
        contactId: contact.id,
      }));
    }

    if (!!payload) {
      await saveEmailCampaignContact((isEditProgress && eventData) ? eventData.id : emailCampaignId, payload)
        .then(() => { setButtonName('Send') })
        .catch((error: any) => {
          console.error('Error saving contact:', error);
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: !isLoading ? {
      name: isEditProgress ? eventData?.name || '' : '',
      subject: isEditProgress ? eventData?.subject : '',
      emailContext: isEditProgress ? eventData?.emailContext || '' : '',
      answers: (Array.isArray(questionAnswer) ? questionAnswer : [])?.map((question: any) => ({
        questionText: question.questionText,
        answerText: isEditProgress
          ? eventData?.selectedAnswers?.find((answer: any) => answer.questionText === question.questionText)?.answerText || ''
          : '',
      })),
      coldEmail: {
        coldEmailContent: isEditProgress ? eventData?.selectedAnswers[0]?.metaData?.Topic || '' : '',
        // coldEmailRepeat: isEditProgress ? eventData?.coldEmail?.coldEmailRepeat || 1 : 1,
        coldEmailLimit: isEditProgress ? eventData?.selectedAnswers[0]?.metaData?.Limit || 1 : 1,
        emailsSendingTime: isEditProgress ? eventData?.selectedAnswers[0]?.metaData?.EmailsSendingTime || [] : [],
      },
    } : {
      name: '',
      subject: '',
      emailContext: '',
      answers: [],
      coldEmail: {
        coldEmailContent: '',
        // coldEmailRepeat: 1,
        coldEmailLimit: 1,
        emailsSendingTime: [{
          index: 0,
          name: 'First email',
          sendAfter: 0,
        }],
      },
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Name"),
    }),
    onSubmit: async (values) => {
      if (currentStep === 1 && (!values.answers[0]?.answerText || !values.answers[1]?.answerText || !values.answers[2]?.answerText)) {
        setShowErrorForm(true);
        return;
      }
      // Check cold email fields if "Cold email outreach" is selected
      const coldEmailAnswer = values.answers?.find((a: any) => a.questionText.includes(CampaignQuestionType.Type) && a.answerText === CampaignType.ColdEmailOutreach);
      const requestReviewAnswer = values.answers?.find((a: any) => a.questionText.includes(CampaignQuestionType.Type) && a.answerText === CampaignType.RequestForReview);
      if (currentStep === 1 && coldEmailAnswer && !values.coldEmail?.coldEmailContent) {
        setShowErrorForm(true);
        return;
      }
      // Check if reviewLink is empty when "Request for review" is selected
      if (currentStep === 1 && requestReviewAnswer && (!reviewLink || reviewLink.trim() === '')) {
        setShowErrorReviewLink(true);
        return;
      }
      else if (currentStep === 5) {
        return;
      }
      else if (currentStep === 3 && !!contactsSelected && contactsSelected.length === 0 && !isViewProgress) {
        setShowErrorSelectContact(true);
        return;
      }
      else {
        if (currentStep === 1 && hasFormValuesChanged()) {
          setEmailTemplate('')

          const payload = {
            id: (isEditProgress && eventData) ? eventData.id : !!emailCampaignId ? emailCampaignId : null,
            name: values.name,
            campaignQuestionAnswer: values.answers,
            emailContext: values.answers?.map((item: any) => `Question: ${item.questionText} Answer: ${item.answerText}`).join('<br>'),
            channel: formik.values?.answers?.find(
              (a: any) =>
                a.answerText === ChannelType.Email ||
                a.answerText === ChannelType.SMS ||
                a.answerText === ChannelType.EmailAndSMS
            )?.answerText,
            metaData: {
              [MetaData.Topic]: values.coldEmail?.coldEmailContent || '',
              // [MetaData.RepeatFrequency]: values.coldEmail?.coldEmailRepeat || 1,
              [MetaData.Limit]: values.coldEmail?.coldEmailLimit || 1,
              [MetaData.EmailsSendingTime]: values.coldEmail?.emailsSendingTime || [
                {
                  index: 0,
                  name: 'First email',
                  sendAfter: 0,
                }
              ],
            },
            coldEmailContent: values.coldEmail?.coldEmailContent || '',
            // coldEmailRepeat: values.coldEmail?.coldEmailRepeat || 1,
            coldEmailLimit: values.coldEmail?.coldEmailLimit || 1,
            emailsSendingTime: values.coldEmail?.emailsSendingTime || [{
              index: 0,
              name: 'First email',
              sendAfter: 0,
            }],
          };

          if (!!payload && !isViewProgress) {
            // Call Api to generate email template with Agent AI
            generateEmailTemplate(payload).then((res: any) => {
              if (res) {
                formik.values.subject = res.subject;
                setDataTemp(res);
                setEmailTemplate(res.emailTemplate);
                setEmailCampaignId(res.id);
                dispatch(getCampaigns());
                setCurrentEmailCampaignData(res);
                setCurrentSubject(res.subject);
                setChannel(res.channel);
              } else {
                return
              }
            })
              .catch((err: any) => err);
          }
        };

        if (currentStep >= 4 && contactsSelected.length > 0) {
          const payload = {
            body: emailTemplate,
            subject: values.subject || 'Email Marketing',
            recipients: contactsSelected.map(contact => ({ id: contact.id, email: contact.email, name: contact.name, phoneNumber: contact?.phoneNumber })),
            channel: channelType,
            isStrategiesType: values.answers[0]?.answerText.includes(CampaignTypeText.ReEngagement)
              || values.answers[0]?.answerText.includes(CampaignTypeText.RequestForReview)
              || values.answers[0]?.answerText.includes(CampaignTypeText.ColdEmailOutreach) || false,
            campaignType: values.answers[0]?.answerText.includes(CampaignTypeText.ReEngagement)
              ? CampaignType.ReEngagement
              : values.answers[0]?.answerText.includes(CampaignTypeText.RequestForReview)
                ? CampaignType.RequestForReview
                : values.answers[0]?.answerText.includes(CampaignTypeText.ColdEmailOutreach)
                  ? CampaignType.ColdEmailOutreach
                  : 4,
            sendType: values.answers[0]?.answerText.includes(CampaignTypeText.RequestForReview) ? 7 : null,
            emailCampaignId: emailCampaignId
          }
          if (!!payload) {
            localStorage.setItem('currentEmailCampaignId', emailCampaignId || '');
            setSendingEmail(true)
            SendCampaignMarketing(payload)
              .then(async () => {
                await handleSaveEmailCampaignContact();
                updateStatusEmailCampaign((isEditProgress && eventData) ? eventData.id : emailCampaignId).then(() => {
                  dispatch(getCampaigns()).then(async () => {
                    localStorage.removeItem('currentEmailCampaignId');
                    // setContactsTemp([]);
                    setCurrentStep(1);
                    setButtonName('Next');
                    cancelProgress();
                    setEmailTemplate('');
                    setCurrentEmailCampaignData(null);
                    setCurrentSubject(null)
                    setChannel('')
                    setEmailCampaignId('');
                    formik.resetForm();
                    setContactsTemp([]);
                    await simulateApiDelay();
                    setSendingEmail(false);
                    toast.success("Email sent successfully!", { autoClose: 2000 });
                  });
                }).catch((error: any) => {
                  console.error('Error refreshing campaigns:', error);
                });
              }).catch((error: any) => {
                console.error('Error sending email:', error);
              });
          }
        }

        setCurrentStep((prevStep) => prevStep + 1);
      }
    },
  });

  const goToPreviousStep = () => {
    if (currentStep === 3) {
      loadContacts(0, true, textSearchContact, zipCodeFilter, rangeFilter, statusName, saleRepName, channelType);
    }

    if (currentStep === 1) {
      return
    }
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const hasFormValuesChanged = useCallback(() => {
    if (isViewProgress) {
      return false;
    }

    const selectedChannel =
      formik.values?.answers?.find(
        (a: any) =>
          a.answerText === ChannelType.Email ||
          a.answerText === ChannelType.SMS ||
          a.answerText === ChannelType.EmailAndSMS
      )?.answerText;

    const originalChannel = (dataTemp?.channel ?? channelType) || '';
    if (originalChannel !== (selectedChannel || '')) {
      return true;
    }

    const originalName = dataTemp?.name ?? (eventData?.name || '');
    if (formik.values?.name !== originalName) {
      return true;
    }

    const originalSubject = dataTemp?.subject ?? (eventData?.subject || '');
    if (formik.values?.subject !== originalSubject) {
      return true;
    }

    const currentColdEmail = formik.values?.coldEmail;
    const originalColdEmail = {
      coldEmailContent: dataTemp?.metaData?.[MetaData.Topic] ?? (eventData?.selectedAnswers?.[0]?.metaData?.Topic || ''),
      coldEmailLimit: dataTemp?.metaData?.[MetaData.Limit] ?? (eventData?.selectedAnswers?.[0]?.metaData?.Limit || 1),
      emailsSendingTime: dataTemp?.metaData?.[MetaData.EmailsSendingTime] ?? (eventData?.selectedAnswers?.[0]?.metaData?.EmailsSendingTime || []),
    };
    if (currentColdEmail?.coldEmailContent !== originalColdEmail.coldEmailContent ||
      currentColdEmail?.coldEmailLimit !== originalColdEmail.coldEmailLimit ||
      JSON.stringify(currentColdEmail?.emailsSendingTime || []) !== JSON.stringify(originalColdEmail.emailsSendingTime)) {
      return true;
    }

    const currentAnswers = formik.values?.answers || [];
    const originalAnswers = dataTemp?.selectedAnswers.filter((x: any) => !x.isDeleted) ?? (eventData?.selectedAnswers.filter((x: any) => !x.isDeleted) || []);

    if (originalAnswers.length === 0)
      return false;

    if (currentAnswers.length !== originalAnswers.length) {
      return true;
    }

    return currentAnswers.some((curr: any) => {
      const orig = originalAnswers.find((o: any) => o.questionText === curr.questionText);
      return !orig || orig.answerText !== curr.answerText;
    });
  }, [isViewProgress, formik.values, dataTemp, eventData, channelType, isEditProgress]);

  const handleCancelProgress = () => {
    if (currentStep === 1 && !isViewProgress && hasFormValuesChanged()) {
      handleShowModal();
      return;
    }
    if (currentStep === 2 && !isViewProgress && contentChanged) {
      handleShowModal();
      return;
    }
    if (currentStep === 3 && contactsSelected.length > 0 && !isViewProgress) {
      handleShowModal();
      return;
    }
    if (currentStep === 4 && !isViewProgress) {
      handleShowModal();
      return;
    }

    setCurrentStep(1);
    setEmailTemplate('');
    setChannel('');
    setEmailCampaignId('');
    setIsEditing(false);
    setCurrentSubject(null)
    setContactsTemp([]);
    formik.resetForm();
    cancelProgress();
    return;
  };

  const handleSaveTemplate = (updatedContent: string) => {
    if ((updatedContent !== '' && updatedContent !== emailTemplate) || formik.values.subject !== currentSubject) {
      setEmailTemplate(updatedContent);
      setIsEditing(false);
      updateEmailCampaign((isEditProgress && eventData) ? eventData.id : emailCampaignId, { name: '', emailTemplate: updatedContent, subject: formik.values.subject })
    }
  };

  const handleShowModal = () => {
    setIsModalDeleteVisible(true);
  };

  const handleHideModal = () => {
    setIsModalDeleteVisible(false);
  };

  const handleDelete = () => {
    cancelProgress();
    setCurrentStep(1);
    setEmailTemplate('');
    setIsEditing(false);
    setChannel('');
    setContactsTemp([]);
    formik.resetForm();
    setIsModalDeleteVisible(false);
  };

  const handleReGenerateTemplate = () => {
    setWaitingReGenerateTemplate(true)
    const payload = {
      id: currentEmailCampaignData.id,
      name: currentEmailCampaignData.name,
      subject: currentEmailCampaignData.subject,
    }
    if (!!payload) {
      generateEmailTemplate(payload).then((res: any) => {
        if (res) {
          setEmailTemplate(res.emailTemplate);
          formik.values.subject = res.subject
          setWaitingReGenerateTemplate(false)
        } else {
          return;
        }
      }).catch((error: any) => {
        console.error('Error regenerating email template:', error);
      });
    }
  }

  const handleContactSearch = (searchData: { searchValue: string; zipCode: string; range: string, status: string[], saleRep: string[] }) => {
    // search by server side
    setTextSearchContact(searchData.searchValue);
    setZipCodeFilter(searchData.zipCode);
    setRangeFilter(searchData.range);
    setStatusName(searchData.status || []);
    setSaleRepName(searchData.saleRep || []);
    // Reset pagination when searching
    setSkipCount(0);
    setHasMore(true);
  }

  useEffect(() => {
    setSkipCount(0);
    setHasMore(true);
    loadContacts(0, true, textSearchContact, zipCodeFilter, rangeFilter, statusName, saleRepName, channelType);
  }, [textSearchContact, zipCodeFilter, rangeFilter, statusName, saleRepName, channelType]);

  const loadContacts = async (skip = 0, reset = false, keyword = "", zipCode = "", range = "", status = "", saleRep = "", channel = "") => {
    setLoadingContacts(true);
    searchByKeyword({ skipCount: skip, maxResultCount: limit, filter: keyword, zipCode: zipCode, range: range, statusName: status, saleRepName: saleRep, channel: channel }).then((res: any) => {
      if (res) {
        setLoadingContacts(false);
        const newContacts = res?.items || [];
        if (newContacts.length < limit) {
          setHasMore(false);
        } else if (reset) {
          setHasMore(true);
        }

        setContactList(prev => reset ? newContacts : [...prev, ...newContacts]);
        setSkipCount(prev => reset ? limit : prev + limit);
        setCount(res?.totalCount || 0);
      }
    }).catch((error: any) => {
      console.error('Error fetching contacts:', error);
      return { data: [] };
    });
  };

  const handleLoadMore = () => {
    if (!loadingContacts && hasMore) {
      loadContacts(skipCount, false, textSearchContact, zipCodeFilter, rangeFilter, statusName, saleRepName, channelType);
    }
  };

  const handleEditTemplateManually = (isEditing: boolean) => {
    setIsEditing(isEditing);
  }

  return (
    <>
      <DeleteModal
        show={isModalDeleteVisible}
        onHide={handleHideModal}
        onDelete={handleDelete}
        title="You have unsaved changes"
        content="Do you want to discard or continue editing?"
        cancelText='Cancel'
        deleteText='Discard'
      />

      {isInProgress && (
        <div className=" bg-white flex flex-col">
          <div className="bg-white border-b border-gray-200 flex-shrink-0">
            <div className="px-4 py-[11px]">
              <div className="flex items-center justify-between space-x-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleCancelProgress();;
                  }}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-base"
                  disabled={sendingEmail}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </button>

                {/* Progress Steps */}
                <div className="flex items-center space-x-6 mt-4">
                  <ProgressIndicator currentStep={currentStep} />
                </div>
                <div></div>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-2 lg:grid-cols-3 w-full'>
            {/* Form Section */}
            <div className='grid col-span-2 w-full sm:col-span-1 lg:col-span-2 px-4 relative h-auto border-r border-gray-200'>
              <form onSubmit={formik.handleSubmit} className="flex-1 flex flex-col min-h-[calc(100vh-160px)] sm:min-h-[calc(100vh-100px)] items-center">
                {currentStep === 1 && (
                  <CampaignSetupStep
                    isView={isViewProgress}
                    formik={formik}
                    onNext={() => { }}
                    onPrevious={() => { }}
                    currentStep={currentStep}
                    buttonName={buttonName}
                    questionAnswer={questionAnswer}
                    showErrorForm={showErrorForm}
                    setShowErrorForm={setShowErrorForm}
                    reviewLink={reviewLink}
                  />
                )}

                {currentStep === 2 && (
                  <ReviewStep
                    isView={isViewProgress}
                    formik={formik}
                    onNext={() => { }}
                    onPrevious={() => { }}
                    currentStep={currentStep}
                    buttonName={buttonName}
                    emailTemplate={emailTemplate}
                    sanitizedTemplate={sanitizedTemplate}
                    isEditing={isEditing}
                    waitingReGenerateTemplate={waitingReGenerateTemplate}
                    isSubjectChanged={isSubjectChanged}
                    currentSubject={currentSubject}
                    channelType={channelType}
                    setIsEditing={handleEditTemplateManually}
                    handleReGenerateTemplate={handleReGenerateTemplate}
                    handleSaveTemplate={handleSaveTemplate}
                    setIsSubjectChanged={setIsSubjectChanged}
                    onContentChange={(hasChanged: boolean) => setContentChanged(hasChanged)}
                  />
                )}

                {currentStep === 3 && (
                  <ContactStep
                    isView={isViewProgress}
                    totalContacts={count}
                    formik={formik}
                    onNext={() => { }}
                    onPrevious={() => { }}
                    currentStep={currentStep}
                    buttonName={buttonName}
                    filteredContact={contactList}
                    contactsSelected={contactsSelected}
                    selectAll={selectAll}
                    showErrorSelectContact={showErrorSelectContact}
                    hasMore={hasMore}
                    loadingContacts={loadingContacts}
                    saleRepOptions={saleRepOptions}
                    contactsTemp={contactsTemp}
                    onLoadMore={handleLoadMore}
                    handleContactSearch={handleContactSearch}
                    handleSelectAll={handleSelectAll}
                    handleContactSelect={handleContactSelect}
                    setShowErrorSelectContact={setShowErrorSelectContact}
                    channelType={channelType}
                  />
                )}
                {currentStep === 4 && (
                  <ConfirmationStep
                    formik={formik}
                    onNext={() => { }}
                    onPrevious={() => { }}
                    currentStep={currentStep}
                    buttonName={buttonName}
                    contactsSelected={contactsSelected}
                    sanitizedTemplate={sanitizedTemplate}
                    channelType={channelType}
                  />
                )}
                {(currentStep === 5) && (
                  <SendingStep sendingEmail={sendingEmail} />
                )}              {/* Buttons */}
                <StepNavigation
                  contentChanged={contentChanged}
                  isView={isViewProgress}
                  currentStep={currentStep}
                  buttonName={buttonName}
                  onPrevious={goToPreviousStep}
                  onSubmit={() => formik.handleSubmit()}
                />
              </form >
            </div>

            <div></div>
          </div>
        </div >
      )}
    </>
  );
};

export default ProgressCampaign;